import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiEdit2, FiMoreHorizontal, FiTrash2 } from "react-icons/fi";
import { AppButton } from "@/components/ui/primitives";
import { CategoryIcon } from "./CategoryIcon";

type Props = {
  name: string;
  actions: { edit: string; view: string; delete: string; menu: string };
  onEdit: () => void;
  onView: () => void;
  onDelete: () => void;
};

export const CategoryActionsMenu = ({ name, actions, onEdit, onView, onDelete }: Props) => {
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const close = (restoreFocus = false) => {
    setPosition(null);
    if (restoreFocus) trigger.current?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (!position) return;
    const outside = (event: PointerEvent) => {
      if (!menu.current?.contains(event.target as Node) && !trigger.current?.contains(event.target as Node)) setPosition(null);
    };
    const dismiss = () => setPosition(null);
    document.addEventListener("pointerdown", outside);
    window.addEventListener("resize", dismiss);
    window.addEventListener("scroll", dismiss, true);
    menu.current?.querySelector<HTMLButtonElement>("button")?.focus({ preventScroll: true });
    return () => {
      document.removeEventListener("pointerdown", outside);
      window.removeEventListener("resize", dismiss);
      window.removeEventListener("scroll", dismiss, true);
    };
  }, [position]);

  return <>
    <button ref={trigger} type="button" className="category-menu-trigger app-focus-ring" aria-label={`${actions.menu}: ${name}`} aria-haspopup="menu" aria-expanded={!!position} onClick={() => {
      if (position) return close();
      const rect = trigger.current!.getBoundingClientRect();
      const width = Math.min(240, window.innerWidth - 24);
      const height = 180;
      setPosition({ left: Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12)), top: Math.max(12, rect.bottom + height + 8 <= window.innerHeight ? rect.bottom + 8 : rect.top - height - 8) });
    }}><FiMoreHorizontal size={22} aria-hidden="true" /></button>
    {position && createPortal(<div ref={menu} className="category-card-menu-items" style={position} role="menu" aria-label={`${actions.menu}: ${name}`} onKeyDown={(event) => {
      const items = [...event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')];
      const index = items.indexOf(document.activeElement as HTMLButtonElement);
      const next = event.key === "ArrowDown" ? (index + 1) % items.length : event.key === "ArrowUp" ? (index - 1 + items.length) % items.length : event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : -1;
      if (next >= 0) { event.preventDefault(); items[next]?.focus(); }
      if (event.key === "Escape") { event.preventDefault(); close(true); }
      if (event.key === "Tab") close(true);
    }}>
      {[{ label: actions.edit, action: onEdit, icon: <FiEdit2 size={22} />, danger: false }, { label: actions.view, action: onView, icon: <CategoryIcon name="receipt" />, danger: false }, { label: actions.delete, action: onDelete, icon: <FiTrash2 size={22} />, danger: true }].map(({ label, action, icon, danger }) => <AppButton key={label} role="menuitem" tone="ghost" className={`category-menu-action${danger ? " category-menu-action--danger" : ""}`} leadingIcon={icon} onClick={() => { close(); action(); }}>{label}</AppButton>)}
    </div>, document.body)}
  </>;
};
